import { dynamicRecordIdProperty } from '../../utils/dynamic-value-property';

/**
 * The properties the typeahead chips and Kendo multiselects read their label from
 * (`[text]="item?.<prop>"` on NativeScript, `[textField]` on web). A placeholder fills them
 * all so it reads the same in every control.
 */
const CHIP_LABEL_PROPERTIES = [
  'Name',
  'CompanyName',
  'CompanyNumber',
  'CustomerName',
  'DisplayName',
  'FullName',
  'JobID',
  'OfficeLocation',
];

type SavedIdRecord = Record<string, unknown>;

/** The chip label for a saved id no search returned (inactive, closed, or offline). */
export function dynamicUnresolvedSavedIdLabel(id: string): string {
  return `${id} (unavailable)`;
}

function placeholderFor(id: string, valueProperty: string): SavedIdRecord {
  const placeholder: SavedIdRecord = {};
  for (const property of CHIP_LABEL_PROPERTIES) {
    placeholder[property] = dynamicUnresolvedSavedIdLabel(id);
  }
  placeholder[valueProperty] = id;
  return placeholder;
}

/**
 * Bug 34978: the records a multi-select shows for its saved ids, in saved order. An id the
 * search did not return (an inactive employee, a closed job) becomes a placeholder that keeps
 * the id under `valueProperty`, so the next save writes it back instead of dropping it.
 * Found records are matched by the same id property the save reads (`model`, Bug 35174).
 */
export function dynamicResolveSavedIds<T extends object>(
  savedIds: Array<string>,
  found: Array<T>,
  valueProperty: string,
  model: Parameters<typeof dynamicRecordIdProperty>[1] = { valueProperty }
): Array<SavedIdRecord> {
  const byId = new Map<string, T>();
  for (const record of found) {
    const key = dynamicRecordIdProperty(record, model) ?? valueProperty;
    const id = (record as SavedIdRecord)[key];
    if (id != null) {
      byId.set(`${id}`.toLowerCase(), record);
    }
  }
  const seen = new Set<string>();
  const items: Array<SavedIdRecord> = [];
  for (const raw of savedIds) {
    const id = `${raw ?? ''}`.trim();
    const key = id.toLowerCase();
    if (!id || seen.has(key)) {
      continue;
    }
    seen.add(key);
    items.push(
      (byId.get(key) as SavedIdRecord | undefined) ??
        placeholderFor(id, valueProperty)
    );
  }
  return items;
}
